import { getMenus } from "@/constants/menu";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { background, divider, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { hexToRgba } from "@/utils/pieces";
import { Drawer, Divider, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useMemo, useCallback } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";

type TAppDrawerProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userType, user } = useInitSetup();

  const menus = useMemo(() => getMenus(userType, user?.companyId), [userType, user?.companyId]);

  // Helper function to check if route is active
  const isRouteActive = useCallback(
    (routes?: string[]) => {
      if (!routes) return false;
      return routes.some((route: string) => Boolean(matchRoutes([{ path: route }], pathname)));
    },
    [pathname],
  );

  const DrawerContent = useMemo(
    () => (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        {menus.map((item, index) => {
          // Handle parent items with children (nested menu)
          if ("children" in item && item.children && Array.isArray(item.children)) {
            const children = item.children;

            if (isLaptop) {
              // On laptop view, render children as individual items without parent
              return (
                <Box key={index}>
                  {children.map((child, childIndex) => {
                    const isChildActive = isRouteActive(child.relatedRoutes);
                    return (
                      <Tooltip title={child.text} placement="right" key={`${index}-${childIndex}`}>
                        <ListItemWrapper isLaptop={isLaptop} className={isChildActive ? "active" : ""} onClick={() => navigate(child.path)}>
                          <Stack flexDirection="row" justifyContent="center" alignItems="center">
                            {child.icon && <child.icon width={24} height={24} />}
                          </Stack>
                        </ListItemWrapper>
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            }
            // On desktop view, render with section header and children
            return (
              <Stack key={index} gap={1}>
                {index > 0 && <Divider sx={{ borderColor: divider.lower, opacity: 0.3 }} />}
                <SectionHeader>
                  <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                    {item.text}
                  </Typography>
                </SectionHeader>
                {children.map((child, childIndex) => {
                  const isChildActive = isRouteActive(child.relatedRoutes);
                  return (
                    <ListItemWrapper
                      isLaptop={isLaptop}
                      key={`${index}-${childIndex}`}
                      className={isChildActive ? "active" : ""}
                      onClick={() => navigate(child.path)}
                    >
                      <Stack flexDirection="row" alignItems="center" gap={1.5}>
                        {child.icon && <child.icon width={24} height={24} />}
                        <Typography variant="body14Bold">{child.text}</Typography>
                      </Stack>
                    </ListItemWrapper>
                  );
                })}
              </Stack>
            );
          }

          // Type guard to ensure item has required properties
          if (!("relatedRoutes" in item) || !("path" in item)) return null;

          const isActive = isRouteActive(item.relatedRoutes);
          if (isLaptop)
            return (
              <Tooltip title={item.text} placement="right" key={item.text}>
                <ListItemWrapper isLaptop={isLaptop} className={isActive ? "active" : ""} onClick={() => navigate(item.path)}>
                  <Stack flexDirection="row" justifyContent="center" alignItems="center">
                    {item.icon && <item.icon width={24} height={24} />}
                  </Stack>
                </ListItemWrapper>
              </Tooltip>
            );
          return (
            <ListItemWrapper isLaptop={isLaptop} key={item.text} className={isActive ? "active" : ""} onClick={() => navigate(item.path)}>
              <Stack flexDirection="row" alignItems="center" gap={1.5}>
                {item.icon && <item.icon width={24} height={24} />}
                <Typography variant="body14Bold">{item.text}</Typography>
              </Stack>
            </ListItemWrapper>
          );
        })}
      </ListWrapper>
    ),
    [pathname, isLaptop, isRouteActive, menus],
  );

  if (!isLaptop) return DrawerContent;
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 56px)", top: "56px" } }}
    >
      {DrawerContent}
    </Drawer>
  );
};

const ListWrapper = styled(Stack)<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${background.main};
  color: ${text.white};
  min-height: calc(100vh - 56px);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 56px;
  z-index: ${zIndex.drawer};
`;

const ListItemWrapper = styled(Box)<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  padding: ${({ isLaptop }) => (isLaptop ? `${spacing.xs} 0px` : `${spacing.xs} ${spacing.sm}`)};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;

const SectionHeader = styled(Box)`
  padding: ${spacing.xxs} ${spacing.xs};
  color: ${text.white};
`;

